'use client';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getVideoClickDataLayer } from '../../utils/gtm';
import styles from './Video.module.scss';
const DEFAULT_REACHED_STATE = [25, 50, 75, 100];
export const setTimer = (videoElement, timeElement) => {
    if (!(videoElement === null || videoElement === void 0 ? void 0 : videoElement.duration) || isNaN(videoElement.currentTime) || !timeElement)
        return;
    const timeLeft = videoElement.duration - videoElement.currentTime;
    const minutes = Math.floor(timeLeft / 60);
    const seconds = Math.floor(timeLeft - minutes * 60);
    const minuteValue = minutes.toString().padStart(2, '0');
    const secondsValue = seconds.toString().padStart(2, '0');
    timeElement.textContent = `${minuteValue}:${secondsValue}`;
};
export const VideoPlayer = ({ video, title, poster, track, loop, analytics, autoPlay, showTimer = false, muted, }) => {
    const videoRef = useRef(null);
    const timerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(autoPlay || false);
    const [reached, setReached] = useState(DEFAULT_REACHED_STATE);
    const toggleVideo = useCallback(() => {
        setIsPlaying((prevIsPlaying) => {
            var _a, _b;
            if (analytics) {
                analytics(getVideoClickDataLayer(prevIsPlaying ? 'pause' : 'play', video, title));
            }
            prevIsPlaying ? (_a = videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) === null || _a === void 0 ? void 0 : _a.pause() : (_b = videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) === null || _b === void 0 ? void 0 : _b.play();
            return !prevIsPlaying;
        });
    }, [analytics, video, title]);
    const handleOnEnded = useCallback(() => {
        if (!loop && (videoRef === null || videoRef === void 0 ? void 0 : videoRef.current)) {
            setIsPlaying(false);
            videoRef.current.currentTime = 0;
            setReached(DEFAULT_REACHED_STATE);
        }
    }, [loop]);
    const handleTimeUpdate = useCallback(() => {
        var _a, _b;
        if (showTimer)
            setTimer(videoRef === null || videoRef === void 0 ? void 0 : videoRef.current, timerRef === null || timerRef === void 0 ? void 0 : timerRef.current);
        if (!analytics)
            return;
        const percentage = Math.ceil(((((_a = videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) === null || _a === void 0 ? void 0 : _a.currentTime) || 1) / (((_b = videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) === null || _b === void 0 ? void 0 : _b.duration) || 1)) * 100);
        if (percentage >= reached[0]) {
            analytics(getVideoClickDataLayer(`reached ${reached[0]}%`, video, title));
            setReached(([, ...prev]) => prev || []);
        }
    }, [reached, video, analytics, showTimer, title]);
    useEffect(() => {
        if ((videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) && !videoRef.current.onended) {
            videoRef.current.onended = handleOnEnded;
        }
        if (videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) {
            videoRef.current.ontimeupdate = handleTimeUpdate;
        }
    }, [videoRef, handleOnEnded, handleTimeUpdate]);
    const initialTimerHandler = () => {
        if (showTimer)
            setTimer(videoRef === null || videoRef === void 0 ? void 0 : videoRef.current, timerRef === null || timerRef === void 0 ? void 0 : timerRef.current);
    };
    return (<div className={styles.havenVideoPlayer}>
      <video ref={videoRef} className={styles.video} loop={loop} poster={poster} autoPlay={autoPlay} onLoadedMetadata={initialTimerHandler} muted={muted}>
        <source src={video} type='video/mp4'/>
        <track src={track} kind='captions' srcLang='en' label='English'/>
      </video>
      <button type='button' onClick={toggleVideo}>
        <div className={cx(styles.bigPlayButton, isPlaying && styles.hide)}/>
        <span className='srOnly'>Play</span>
      </button>
      <div />
      {showTimer ? (<div className={styles.timer}>
          <span ref={timerRef} aria-label='timer' role='timer'>
            00:00
          </span>
        </div>) : null}
    </div>);
};
